"use client";

import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { MessageSquare, Edit2, Trash2, MoreVerticalIcon, Pin, PinOff, ChevronDown, ChevronUp, CreditCard, LogOut, ExternalLink, Sparkles, Trophy } from "lucide-react";
// import dynamic from "next/dynamic";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { List, type RowComponentProps } from "react-window";
import { toast } from "sonner";
import { CreditsDisplay } from "@/components/credits/credits-display";
import { ThemeToggle } from "@/components/landing/theme-toggle";
import { SidebarLoungePoints } from "@/components/lounge/sidebar-points";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarMenu, SidebarMenuAction, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import { Wordmark } from "@/components/ui/wordmark";
import { useChats, useDeleteChat, useUpdateChat, type Chat } from "@/hooks/useChats";
import { useOrganization } from "@/hooks/useOrganization";
import { useUser } from "@/hooks/useUser";
import { clearLastUsedProjectCookiesAction } from "@/lib/actions/project";
import { useAuth } from "@/lib/auth-client";
import { withOrgParam } from "@/lib/utils";
import { ChatSidebarSkeleton } from "./chat-sidebar-skeleton";
import { OrganizationSwitcher } from "./organization-switcher";
import { SidebarChatSearch, SidebarNewAction } from "./sidebar-actions";
import { StudioNav } from "./studio-nav";
// import { ProjectSwitcher } from "./project-switcher";

import type { Organization, Project } from "@/lib/types";

// const OrganizationSwitcher = dynamic(
// 	() => import("./organization-switcher").then((m) => m.OrganizationSwitcher),
// 	{ ssr: false },
// );

export interface ChatSidebarHandle {
  scrollToTop: () => void;
}
interface ChatSidebarProps {
  currentChatId?: string;
  onChatSelect?: (chatId: string) => void;
  onNewChat?: () => void;
  className?: string;
  isLoading?: boolean;
  organizations: Organization[];
  selectedOrganization: Organization | null;
  onSelectOrganization: (organization: Organization | null) => void;
  onOrganizationCreated: (organization: Organization) => void;
  projects: Project[];
  selectedProject: Project | null;
  onSelectProject: (project: Project | null) => void;
  onProjectCreated: (project: Project) => void;
}
type ChatHistoryRow = {
  type: "header";
  key: string;
  title: string;
} | {
  type: "chat";
  key: string;
  chat: Chat;
} | {
  type: "spacer";
  key: string;
};
const ROW_HEIGHT_HEADER = 28;
const ROW_HEIGHT_SPACER = 6;
const ROW_HEIGHT_CHAT = 52;
const HISTORY_COLLAPSED_STORAGE_KEY = "playground-chat-history-collapsed";
interface ChatHistoryRowProps {
  rows: ChatHistoryRow[];
  currentChatId?: string;
  editingId: string | null;
  editTitle: string;
  pendingFocusChatId: string | null;
  isPageLoading: boolean;
  isMobile: boolean;
  onChatSelect?: (chatId: string) => void;
  onEditTitleChange: (value: string) => void;
  onSaveTitle: (chatId: string) => void;
  onCancelEdit: () => void;
  onDeleteChat: (chatId: string) => void;
  onTogglePin: (chat: Chat) => void;
  onStartEdit: (chat: Chat) => void;
  onEditFocused: () => void;
}
function getChatHistoryRowHeight(index: number, {
  rows
}: ChatHistoryRowProps): number {
  const row = rows[index];
  if (row?.type === "header") {
    return ROW_HEIGHT_HEADER;
  }
  if (row?.type === "spacer") {
    return ROW_HEIGHT_SPACER;
  }
  return ROW_HEIGHT_CHAT;
}
function EditChatTitleInput(t0) {
  const $ = _c(16);
  const {
    chatId,
    value,
    shouldFocus,
    onChange,
    onSave,
    onCancel,
    onFocused
  } = t0;
  const inputRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== onFocused || $[1] !== shouldFocus) {
    t1 = () => {
      if (!shouldFocus || !inputRef.current || document.activeElement === inputRef.current) {
        return;
      }
      const el = inputRef.current;
      el.focus();
      const len = el.value.length;
      el.setSelectionRange(len, len);
      onFocused();
    };
    t2 = [shouldFocus, onFocused];
    $[0] = onFocused;
    $[1] = shouldFocus;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== onChange) {
    t3 = e => onChange(e.target.value);
    $[4] = onChange;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== chatId || $[7] !== onSave) {
    t4 = () => onSave(chatId);
    $[6] = chatId;
    $[7] = onSave;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== onCancel) {
    t5 = e_0 => {
      if (e_0.key === "Enter") {
        e_0.preventDefault();
        e_0.currentTarget.blur();
      }
      if (e_0.key === "Escape") {
        e_0.preventDefault();
        onCancel();
      }
    };
    $[9] = onCancel;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t3 || $[12] !== t4 || $[13] !== t5 || $[14] !== value) {
    t6 = <Input ref={inputRef} value={value} onChange={t3} onBlur={t4} onKeyDown={t5} className="h-7 text-sm border-none px-1 focus-visible:ring-0 bg-transparent" />;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = value;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}
type ChatHistoryItemProps = Pick<ChatHistoryRowProps, "currentChatId" | "editingId" | "editTitle" | "pendingFocusChatId" | "isPageLoading" | "isMobile" | "onChatSelect" | "onEditTitleChange" | "onSaveTitle" | "onCancelEdit" | "onDeleteChat" | "onTogglePin" | "onStartEdit" | "onEditFocused"> & {
  chat: Chat;
};
function ChatHistoryItem(t0) {
  const $ = _c(26);
  const {
    chat,
    currentChatId,
    editingId,
    editTitle,
    pendingFocusChatId,
    isPageLoading,
    isMobile,
    onChatSelect,
    onEditTitleChange,
    onSaveTitle,
    onCancelEdit,
    onDeleteChat,
    onTogglePin,
    onStartEdit,
    onEditFocused
  } = t0;
  const isEditing = editingId === chat.id;
  const isActive = currentChatId === chat.id;
  const isActionFocusable = isActive && isMobile ? undefined : -1;
  let t1;
  if ($[0] !== chat.id || $[1] !== chat.messageCount || $[2] !== chat.title || $[3] !== chat.updatedAt || $[4] !== editTitle || $[5] !== isActive || $[6] !== isEditing || $[7] !== isPageLoading || $[8] !== onCancelEdit || $[9] !== onChatSelect || $[10] !== onEditFocused || $[11] !== onEditTitleChange || $[12] !== onSaveTitle || $[13] !== pendingFocusChatId) {
    t1 = isEditing ? <div className="flex h-full w-full items-center rounded-md px-2 pr-8 text-left text-sm ring-sidebar-ring bg-sidebar-accent text-sidebar-accent-foreground"><EditChatTitleInput chatId={chat.id} value={editTitle} shouldFocus={pendingFocusChatId === chat.id} onChange={onEditTitleChange} onSave={onSaveTitle} onCancel={onCancelEdit} onFocused={onEditFocused} /></div> : <SidebarMenuButton isActive={isActive} onClick={() => onChatSelect?.(chat.id)} className={["h-full! w-full justify-start group relative pr-2 !transition-none group-hover/chat-row:pr-[4.5rem]", isActive ? "max-md:pr-[4.5rem]" : ""].join(" ")} type="button" disabled={isPageLoading}><div className="flex-1 min-w-0"><div className="truncate text-sm font-medium mb-0.5">{chat.title}</div><div className="text-xs text-muted-foreground">{chat.messageCount} messages • {formatDate(chat.updatedAt)}</div></div></SidebarMenuButton>;
    $[0] = chat.id;
    $[1] = chat.messageCount;
    $[2] = chat.title;
    $[3] = chat.updatedAt;
    $[4] = editTitle;
    $[5] = isActive;
    $[6] = isEditing;
    $[7] = isPageLoading;
    $[8] = onCancelEdit;
    $[9] = onChatSelect;
    $[10] = onEditFocused;
    $[11] = onEditTitleChange;
    $[12] = onSaveTitle;
    $[13] = pendingFocusChatId;
    $[14] = t1;
  } else {
    t1 = $[14];
  }
  let t2;
  if ($[15] !== chat || $[16] !== isActionFocusable || $[17] !== isActive || $[18] !== isEditing || $[19] !== onDeleteChat || $[20] !== onStartEdit || $[21] !== onTogglePin) {
    t2 = !isEditing && <div className="absolute right-1 top-1/2 flex -translate-y-1/2 items-center gap-0.5"><button type="button" aria-label={chat.pinned ? "Unpin chat" : "Pin chat"} title={chat.pinned ? "Unpin chat" : "Pin chat"} tabIndex={isActionFocusable} onClick={e => {
        e.stopPropagation();
        onTogglePin(chat);
      }} className={["flex h-7 w-7 items-center justify-center rounded-md text-sidebar-foreground outline-hidden transition-opacity hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 focus-visible:ring-sidebar-ring", "pointer-events-none opacity-0 group-hover/chat-row:pointer-events-auto group-hover/chat-row:opacity-100", isActive ? "max-md:pointer-events-auto max-md:opacity-100" : ""].join(" ")}>{chat.pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}</button><DropdownMenu><DropdownMenuTrigger asChild={true}><SidebarMenuAction type="button" tabIndex={isActionFocusable} onClick={_temp} className={["pointer-events-none static hidden h-7 w-7 cursor-pointer opacity-0 group-hover/chat-row:flex group-hover/chat-row:pointer-events-auto group-hover/chat-row:opacity-100 data-[state=open]:flex data-[state=open]:pointer-events-auto data-[state=open]:opacity-100", isActive ? "max-md:flex max-md:pointer-events-auto max-md:opacity-100" : ""].join(" ")}><MoreVerticalIcon className="h-3.5 w-3.5" /></SidebarMenuAction></DropdownMenuTrigger><DropdownMenuContent align="end" className="w-48"><DropdownMenuItem onClick={e_1 => {
            e_1.stopPropagation();
            onTogglePin(chat);
          }} className="flex items-center gap-2">{chat.pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}{chat.pinned ? "Unpin" : "Pin"}</DropdownMenuItem><DropdownMenuItem onClick={e_2 => {
            e_2.stopPropagation();
            onStartEdit(chat);
          }} className="flex items-center gap-2"><Edit2 className="h-4 w-4" />Rename</DropdownMenuItem><DropdownMenuItem onClick={e_3 => {
            e_3.stopPropagation();
            onDeleteChat(chat.id);
          }} className="flex items-center gap-2 text-destructive focus:text-destructive"><Trash2 className="h-4 w-4" />Delete</DropdownMenuItem></DropdownMenuContent></DropdownMenu></div>;
    $[15] = chat;
    $[16] = isActionFocusable;
    $[17] = isActive;
    $[18] = isEditing;
    $[19] = onDeleteChat;
    $[20] = onStartEdit;
    $[21] = onTogglePin;
    $[22] = t2;
  } else {
    t2 = $[22];
  }
  let t3;
  if ($[23] !== t1 || $[24] !== t2) {
    t3 = <div className="relative h-full px-2 pb-1"><div className="group/chat-row relative h-full">{t1}{t2}</div></div>;
    $[23] = t1;
    $[24] = t2;
    $[25] = t3;
  } else {
    t3 = $[25];
  }
  return t3;
}
function _temp(e_0) {
  e_0.stopPropagation();
}
function ChatHistoryRowComponent(t0) {
  const $ = _c(28);
  const {
    ariaAttributes,
    index,
    style,
    rows,
    currentChatId,
    editingId,
    editTitle,
    pendingFocusChatId,
    isPageLoading,
    isMobile,
    onChatSelect,
    onEditTitleChange,
    onSaveTitle,
    onCancelEdit,
    onDeleteChat,
    onTogglePin,
    onStartEdit,
    onEditFocused
  } = t0;
  const row = rows[index];
  if (!row) {
    return null;
  }
  if (row.type === "spacer") {
    let t1;
    if ($[0] !== style) {
      t1 = <div style={style} aria-hidden="true" />;
      $[0] = style;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (row.type === "header") {
    let t1;
    if ($[2] !== row.title) {
      t1 = <div className="px-5 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider group-data-[collapsible=icon]:hidden">{row.title}</div>;
      $[2] = row.title;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    let t2;
    if ($[4] !== ariaAttributes || $[5] !== style || $[6] !== t1) {
      t2 = <div {...ariaAttributes} style={style}>{t1}</div>;
      $[4] = ariaAttributes;
      $[5] = style;
      $[6] = t1;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    return t2;
  }
  let t1;
  if ($[8] !== currentChatId || $[9] !== editTitle || $[10] !== editingId || $[11] !== isMobile || $[12] !== isPageLoading || $[13] !== onCancelEdit || $[14] !== onChatSelect || $[15] !== onDeleteChat || $[16] !== onEditFocused || $[17] !== onEditTitleChange || $[18] !== onSaveTitle || $[19] !== onStartEdit || $[20] !== onTogglePin || $[21] !== pendingFocusChatId || $[22] !== row.chat) {
    t1 = <ChatHistoryItem chat={row.chat} currentChatId={currentChatId} editingId={editingId} editTitle={editTitle} pendingFocusChatId={pendingFocusChatId} isPageLoading={isPageLoading} isMobile={isMobile} onChatSelect={onChatSelect} onEditTitleChange={onEditTitleChange} onSaveTitle={onSaveTitle} onCancelEdit={onCancelEdit} onDeleteChat={onDeleteChat} onTogglePin={onTogglePin} onStartEdit={onStartEdit} onEditFocused={onEditFocused} />;
    $[8] = currentChatId;
    $[9] = editTitle;
    $[10] = editingId;
    $[11] = isMobile;
    $[12] = isPageLoading;
    $[13] = onCancelEdit;
    $[14] = onChatSelect;
    $[15] = onDeleteChat;
    $[16] = onEditFocused;
    $[17] = onEditTitleChange;
    $[18] = onSaveTitle;
    $[19] = onStartEdit;
    $[20] = onTogglePin;
    $[21] = pendingFocusChatId;
    $[22] = row.chat;
    $[23] = t1;
  } else {
    t1 = $[23];
  }
  let t2;
  if ($[24] !== ariaAttributes || $[25] !== style || $[26] !== t1) {
    t2 = <div {...ariaAttributes} style={style}>{t1}</div>;
    $[24] = ariaAttributes;
    $[25] = style;
    $[26] = t1;
    $[27] = t2;
  } else {
    t2 = $[27];
  }
  return t2;
}
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
  if (diffInHours < 1) {
    return "Just now";
  } else if (diffInHours < 24) {
    return `${Math.floor(diffInHours)}h ago`;
  } else if (diffInHours < 48) {
    return "Yesterday";
  } else {
    return format(date, "MMM d");
  }
}
function groupChatsByDate(chats: Chat[]) {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const lastWeek = new Date(today);
  lastWeek.setDate(lastWeek.getDate() - 7);
  const groups = {
    today: [] as Chat[],
    yesterday: [] as Chat[],
    lastWeek: [] as Chat[],
    older: [] as Chat[]
  };
  chats.forEach(chat => {
    const chatDate = new Date(chat.updatedAt);
    if (chatDate.toDateString() === today.toDateString()) {
      groups.today.push(chat);
    } else if (chatDate.toDateString() === yesterday.toDateString()) {
      groups.yesterday.push(chat);
    } else if (chatDate >= lastWeek) {
      groups.lastWeek.push(chat);
    } else {
      groups.older.push(chat);
    }
  });
  return groups;
}
export const ChatSidebar = function ChatSidebar({
  ref,
  currentChatId,
  onChatSelect,
  onNewChat,
  className,
  isLoading: isPageLoading = false,
  organizations,
  selectedOrganization,
  onSelectOrganization
}: ChatSidebarProps & {
  ref?: React.RefObject<ChatSidebarHandle | null>;
}) {
  const listContainerRef = useRef<HTMLDivElement | null>(null);
  useImperativeHandle(ref, () => ({
    scrollToTop: () => {
      const scrollEl = listContainerRef.current?.firstElementChild as HTMLElement | null;
      scrollEl?.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    }
  }));
  const queryClient = useQueryClient();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  // Preserve the selected organization across playground navigation so users
  // don't have to re-pick their org on every page.
  const orgIdParam = searchParams.get("orgId");
  const withOrg = (path: string) => withOrgParam(path, orgIdParam);
  const posthog = usePostHog();
  const {
    state: sidebarState,
    isMobile,
    setOpenMobile
  } = useSidebar();
  const showOrganizationSwitcher = pathname === "/" || pathname === "/group";
  const {
    user,
    isLoading: isUserLoading
  } = useUser();
  const {
    signOut
  } = useAuth();
  const {
    organization,
    isLoading: isOrgLoading
  } = useOrganization();

  // Resolve the org context for chat history: the selected org, or the
  // dedicated Chat org (backing the "Chat plan" context) when none is selected.
  const resolvedOrgId = selectedOrganization?.id ?? organization?.id;

  // Use real chat data from API, scoped to the resolved organization context.
  const {
    data: chatsData,
    isLoading: isChatsLoading
  } = useChats(resolvedOrgId);
  const deleteChat = useDeleteChat();
  const updateChat = useUpdateChat();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");
  // chatId that needs initial focus. Cleared once the row delivers focus,
  // so re-mounting the row on scroll never re-steals focus mid-edit.
  const [pendingFocusChatId, setPendingFocusChatId] = useState<string | null>(null);
  const [isHistoryCollapsed, setIsHistoryCollapsed] = useState(() => typeof document !== "undefined" && document.cookie.split("; ").includes(`${HISTORY_COLLAPSED_STORAGE_KEY}=1`));
  // Transitions stay off until after hydration so a cookie-collapsed list
  // doesn't animate shut on every page load.
  const [historyTransitionReady, setHistoryTransitionReady] = useState(false);
  useEffect(() => {
    setHistoryTransitionReady(true);
  }, []);
  const toggleHistoryCollapsed = useCallback(() => {
    setIsHistoryCollapsed(prev => {
      const next = !prev;
      document.cookie = `${HISTORY_COLLAPSED_STORAGE_KEY}=${next ? "1" : "0"}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax`;
      return next;
    });
  }, []);
  const chats = useMemo(() => chatsData?.chats ?? [], [chatsData?.chats]);
  const logout = async () => {
    posthog.reset();

    // Clear last used project cookies before signing out
    try {
      await clearLastUsedProjectCookiesAction();
    } catch {
      toast.error("Failed to clear last used project cookies");
    }
    await signOut({
      fetchOptions: {
        onSuccess: () => {
          queryClient.clear();
          router.push(process.env.NODE_ENV === "development" ? "http://localhost:3003/login" : "https://chat.llmgateway.io/login");
        }
      }
    });
  };
  const handleEditTitle = useCallback((chat: Chat) => {
    setEditingId(chat.id);
    setEditTitle(chat.title);
    setPendingFocusChatId(chat.id);
  }, []);
  const saveTitle = useCallback((chatId: string) => {
    const nextTitle = editTitle.trim();
    const currentTitle = chats.find(chat_0 => chat_0.id === chatId)?.title.trim();
    if (nextTitle && nextTitle !== currentTitle) {
      updateChat.mutate({
        params: {
          path: {
            id: chatId
          }
        },
        body: {
          title: nextTitle
        }
      });
    }
    setEditingId(null);
    setEditTitle("");
    setPendingFocusChatId(null);
  }, [chats, editTitle, updateChat]);
  const cancelEditTitle = useCallback(() => {
    setEditingId(null);
    setEditTitle("");
    setPendingFocusChatId(null);
  }, []);
  const handleDeleteChat = useCallback((chatId_0: string) => {
    deleteChat.mutate({
      params: {
        path: {
          id: chatId_0
        }
      }
    });
    if (currentChatId === chatId_0) {
      onNewChat?.();
    }
  }, [deleteChat, currentChatId, onNewChat]);
  const handleChatSelect = useCallback((chatId_1: string) => {
    onChatSelect?.(chatId_1);
    if (isMobile) {
      setOpenMobile(false);
    }
  }, [isMobile, onChatSelect, setOpenMobile]);
  const handleTogglePin = useCallback((chat_1: Chat) => {
    updateChat.mutate({
      params: {
        path: {
          id: chat_1.id
        }
      },
      body: {
        pinned: !chat_1.pinned
      }
    });
  }, [updateChat]);
  const onEditFocused = useCallback(() => {
    setPendingFocusChatId(null);
  }, []);
  const sortedChats = useMemo(() => [...chats].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()), [chats]);
  const pinnedChats = useMemo(() => sortedChats.filter(chat_2 => chat_2.pinned), [sortedChats]);
  const chatGroups = useMemo(() => groupChatsByDate(sortedChats.filter(chat_3 => !chat_3.pinned)), [sortedChats]);
  const historyRows = useMemo<ChatHistoryRow[]>(() => {
    const groups: Array<{
      title: string;
      chats: Chat[];
    }> = [{
      title: "Today",
      chats: chatGroups.today
    }, {
      title: "Yesterday",
      chats: chatGroups.yesterday
    }, {
      title: "Last 7 days",
      chats: chatGroups.lastWeek
    }, {
      title: "Older",
      chats: chatGroups.older
    }];
    const rows: ChatHistoryRow[] = [];

    // Pinned chats render in a separate sticky block above the scrollable
    // list (see render), so they are intentionally excluded here.
    groups.forEach(({
      title,
      chats: groupedChats
    }, groupIndex) => {
      if (groupedChats.length === 0) {
        return;
      }
      rows.push({
        type: "header",
        key: `header-${title}`,
        title
      });
      groupedChats.forEach(chat_4 => {
        rows.push({
          type: "chat",
          key: `chat-${chat_4.id}`,
          chat: chat_4
        });
      });
      const hasNextGroupWithChats = groups.slice(groupIndex + 1).some(group => group.chats.length > 0);
      if (hasNextGroupWithChats) {
        rows.push({
          type: "spacer",
          key: `spacer-${title}`
        });
      }
    });
    return rows;
  }, [chatGroups]);
  const rowProps = useMemo<ChatHistoryRowProps>(() => ({
    rows: historyRows,
    currentChatId,
    editingId,
    editTitle,
    pendingFocusChatId,
    isPageLoading,
    isMobile,
    onChatSelect: handleChatSelect,
    onEditTitleChange: setEditTitle,
    onSaveTitle: saveTitle,
    onCancelEdit: cancelEditTitle,
    onDeleteChat: handleDeleteChat,
    onTogglePin: handleTogglePin,
    onStartEdit: handleEditTitle,
    onEditFocused
  }), [historyRows, currentChatId, editingId, editTitle, pendingFocusChatId, isPageLoading, isMobile, handleChatSelect, saveTitle, cancelEditTitle, handleDeleteChat, handleTogglePin, handleEditTitle, onEditFocused]);
  const isAuthenticated = !!user;
  const isHistoryHidden = sidebarState === "collapsed" && !isMobile;

  // Loading auth state → show lightweight skeleton to avoid hydration issues
  if (isUserLoading) {
    return <ChatSidebarSkeleton organization={null} isOrgLoading={true} />;
  }

  // Unauthenticated → show CTA instead of org/project/chats UI
  if (!isAuthenticated) {
    return <Sidebar className={className}>
				<SidebarHeader>
					<div className="flex flex-col items-center gap-4 mb-4">
						<Link href="/" className="flex self-start items-center gap-2 my-2" prefetch={true}>
							<Wordmark />
						</Link>
						<div className="w-full rounded-md border p-4 text-sm">
							<div className="font-medium mb-2">Sign in required</div>
							<p className="text-muted-foreground mb-3">
								Please sign in to view organizations, projects, and chats.
							</p>
							<div className="flex items-center justify-end gap-2">
								<Button size="sm" asChild>
									<Link href="/login">Sign in</Link>
								</Button>
								<Button size="sm" variant="outline" asChild>
									<Link href="/signup">Create account</Link>
								</Button>
							</div>
						</div>
					</div>
					<StudioNav />
				</SidebarHeader>
			</Sidebar>;
  }
  if (isChatsLoading || isOrgLoading) {
    return <ChatSidebarSkeleton organization={selectedOrganization} isOrgLoading={isOrgLoading} />;
  }
  return <Sidebar collapsible="icon" className={className + " max-md:hidden"}>
			<SidebarHeader>
				<SidebarMenu>
					<SidebarMenuItem>
						<SidebarMenuButton size="lg" asChild tooltip="Lounge">
							<Link href={withOrg("/")} prefetch={true}>
								<Wordmark size="sm" iconBox />
							</Link>
						</SidebarMenuButton>
					</SidebarMenuItem>
					<SidebarChatSearch disabled={pathname !== "/"} onChatSelect={handleChatSelect} onNewChat={onNewChat} />
					<SidebarNewAction label="New Chat" onAction={onNewChat} isLoading={isPageLoading} />
				</SidebarMenu>
				<StudioNav />
			</SidebarHeader>

			<SidebarContent className="overflow-hidden pb-2">
				{/* <SidebarMenu>
     <SidebarMenuItem>
     	<OrganizationSwitcher
     		organizations={organizations}
     		selectedOrganization={selectedOrganization}
     		onSelectOrganization={onSelectOrganization}
     		onOrganizationCreated={onOrganizationCreated}
     	/>
     </SidebarMenuItem>
     </SidebarMenu>
     <SidebarMenu>
     <SidebarMenuItem>
     	{selectedOrganization && (
     		<ProjectSwitcher
     			projects={projects}
     			selectedProject={selectedProject}
     			onSelectProject={onSelectProject}
     			currentOrganization={selectedOrganization}
     			onProjectCreated={onProjectCreated}
     		/>
     	)}
     </SidebarMenuItem>
     </SidebarMenu> */}
				<div>
					<div className="mx-2 mb-2 border-t border-sidebar-border" />
					{organizations.length > 0 && showOrganizationSwitcher ? <>
							<SidebarMenu className="px-2 pb-2 group-data-[collapsible=icon]:hidden">
								<SidebarMenuItem>
									<OrganizationSwitcher organizations={organizations} selectedOrganization={selectedOrganization} onSelectOrganization={onSelectOrganization} />
								</SidebarMenuItem>
							</SidebarMenu>
						</> : null}
				</div>
				<div ref={listContainerRef} aria-hidden={isHistoryHidden} className="flex min-h-0 flex-1 flex-col transition-opacity duration-200 ease-linear group-data-[collapsible=icon]:pointer-events-none group-data-[collapsible=icon]:opacity-0">
					<button type="button" onClick={toggleHistoryCollapsed} aria-expanded={!isHistoryCollapsed} className="flex w-full shrink-0 items-center justify-between px-5 py-2 text-xs font-medium uppercase tracking-wider text-muted-foreground transition-colors hover:text-foreground group-data-[collapsible=icon]:hidden">
						<span>Chats</span>
						<ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${isHistoryCollapsed ? "-rotate-90" : ""}`} />
					</button>
					{/* Collapse animates height so the rotating chevron's promise is
         kept; grid-rows keeps the transition interruptible. */}
					<div className={`grid min-h-0 flex-1 ${historyTransitionReady ? "transition-[grid-template-rows,opacity] duration-200 ease-out motion-reduce:transition-[opacity]" : ""} ${isHistoryCollapsed ? "grid-rows-[0fr] opacity-0" : "grid-rows-[1fr] opacity-100"}`}>
						<div className="flex min-h-0 flex-col overflow-hidden" aria-hidden={isHistoryCollapsed} {...isHistoryCollapsed ? {
            inert: true
          } : {}}>
							{chats.length === 0 ? <div className="flex flex-col items-center justify-center py-8 text-center">
									<MessageSquare className="h-12 w-12 text-muted-foreground/50 mb-4" />
									<p className="text-sm text-muted-foreground mb-2">
										No chat history
									</p>
									<p className="text-xs text-muted-foreground">
										Start a new conversation to see it here
									</p>
								</div> : <>
									{pinnedChats.length > 0 && <div className="shrink-0 max-h-[45%] overflow-y-auto border-b border-sidebar-border/60 pb-1">
											<div className="px-5 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider group-data-[collapsible=icon]:hidden">
												Pinned
											</div>
											{pinnedChats.map(chat_5 => <div key={chat_5.id} style={{
                  height: ROW_HEIGHT_CHAT
                }}>
													<ChatHistoryItem chat={chat_5} currentChatId={currentChatId} editingId={editingId} editTitle={editTitle} pendingFocusChatId={pendingFocusChatId} isPageLoading={isPageLoading} isMobile={isMobile} onChatSelect={handleChatSelect} onEditTitleChange={setEditTitle} onSaveTitle={saveTitle} onCancelEdit={cancelEditTitle} onDeleteChat={handleDeleteChat} onTogglePin={handleTogglePin} onStartEdit={handleEditTitle} onEditFocused={onEditFocused} />
												</div>)}
										</div>}
									{historyRows.length > 0 && <List className="min-h-0 w-full flex-1" style={{
                width: "100%"
              }} rowComponent={ChatHistoryRowComponent} rowCount={historyRows.length} rowHeight={getChatHistoryRowHeight} rowProps={rowProps} overscanCount={8} />}
								</>}
						</div>
					</div>
				</div>
			</SidebarContent>

			<SidebarFooter>
				<SidebarLoungePoints />
				<div className="group-data-[collapsible=icon]:hidden">
					<CreditsDisplay organization={!selectedOrganization || selectedOrganization.kind === "chat" ? organization : selectedOrganization} isLoading={isOrgLoading} isChatPlanOrg={!selectedOrganization || selectedOrganization.kind === "chat"} />
				</div>
				<SidebarMenu>
					<SidebarMenuItem>
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<SidebarMenuButton size="lg" tooltip={user?.name ?? "User"} className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">
									<div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
										<span className="text-xs font-semibold">
											{user?.name?.split(" ").map((n: string) => n[0]).join("").toUpperCase().slice(0, 2) ?? "U"}
										</span>
									</div>
									<div className="grid flex-1 text-left text-sm leading-tight">
										<span className="truncate font-semibold">{user?.name}</span>
										<span className="truncate text-xs text-muted-foreground">
											{user?.email}
										</span>
									</div>
									<ChevronUp className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />
								</SidebarMenuButton>
							</DropdownMenuTrigger>
							<DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg" side="top" align="end" sideOffset={4}>
								<DropdownMenuItem asChild>
									<Link href="/profile" prefetch={true}>
										<Sparkles className="mr-2 h-4 w-4" />
										Profile &amp; Points
									</Link>
								</DropdownMenuItem>
								<DropdownMenuItem asChild>
									<Link href="/leaderboard" prefetch={true}>
										<Trophy className="mr-2 h-4 w-4" />
										Leaderboard
									</Link>
								</DropdownMenuItem>
								<DropdownMenuItem asChild>
									<Link href="/pricing" prefetch={true}>
										<CreditCard className="mr-2 h-4 w-4" />
										Membership &amp; Billing
									</Link>
								</DropdownMenuItem>
								<DropdownMenuSeparator />
								<DropdownMenuItem asChild>
									<a href={process.env.NODE_ENV === "development" ? "http://localhost:3002/dashboard" : "https://llmgateway.io/dashboard"} target="_blank" rel="noopener noreferrer">
										<ExternalLink className="mr-2 h-4 w-4" />
										Dashboard
									</a>
								</DropdownMenuItem>
								<DropdownMenuSeparator />
								<DropdownMenuItem className="justify-between gap-3" onSelect={event => event.preventDefault()}>
									<span>Theme</span>
									<div onClick={event_0 => event_0.stopPropagation()} onKeyDown={event_1 => event_1.stopPropagation()}>
										<ThemeToggle className="shrink-0" size="compact" />
									</div>
								</DropdownMenuItem>
								<DropdownMenuSeparator />
								<DropdownMenuItem onClick={logout}>
									<LogOut className="mr-2 h-4 w-4" />
									Log out
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					</SidebarMenuItem>
				</SidebarMenu>
			</SidebarFooter>
		</Sidebar>;
};